import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    type UpdateUserFormSchemaValues,
    updateUserFormSchema,
    useUpdateUser,
} from 'src/features/profile/api/useUpdateUser';
import { languageOptions, settingsMessages } from 'src/features/profile/utils';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockSubtitle,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalErrorMessages, generalLabelMessages, generalMessages } from 'src/utils/messages';
import { storage } from 'src/utils/storage';

type ProfileInfoFormProps = {
    userId: string;
};

const ProfileSettingsForm = ({ userId }: ProfileInfoFormProps) => {
    const { formatMessage } = useIntl();
    const { toast } = useToast();
    const lang = storage.getLanguage();

    const form = useForm<UpdateUserFormSchemaValues>({
        resolver: zodResolver(updateUserFormSchema),
        defaultValues: {
            lang,
        },
    });

    const { mutate, isPending } = useUpdateUser({
        onSuccess: ({ lang }) => {
            storage.setLanguage(lang);
            form.reset({ lang });
            window.location.reload();
        },
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            }
        },
    });

    // This is currently a bug in all of our forms: The form is not validating the initial values because the form fields are not mounted yet. This is a workaround to trigger the validation after the form fields are mounted.
    // Another solution is to simply remove the isValid check from the submit button
    React.useEffect(() => {
        void form.trigger();
    }, []);

    const onSubmit = (values: UpdateUserFormSchemaValues) => {
        mutate({ userId, ...values });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(settingsMessages.language)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>{formatMessage(settingsMessages.languageSubtitle)}</FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="lang"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Select onValueChange={field.onChange} defaultValue={field.value?.toString()}>
                                        <SelectTrigger>
                                            <SelectValue
                                                placeholder={formatMessage(settingsMessages.changeLanguagePlacholder)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {languageOptions.map((language) => {
                                                return (
                                                    <SelectItem key={language.value} value={language.value}>
                                                        {formatMessage(language.label)}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <Button
                className="sm:place-self-end"
                type="submit"
                disabled={!form.formState.isDirty || !form.formState.isValid || isPending}
            >
                {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                {formatMessage(generalMessages.saveChanges)}
            </Button>
        </Form>
    );
};

export default ProfileSettingsForm;
